import React from "react";
import cx from "classnames";
import { upperFirst } from "lodash-es";

import styles from "./progress.module.scss";

export namespace Progress {
  export type Props = {
    className?: any;
    max?: number;
    min?: number;
    rounded?: boolean;
    size?: "small" | "large";
    theme?: "primary" | "secondary";
    value?: number;
    variant?: "determinate" | "indeterminate";
  };
}

export const Progress = ({
  className,
  max = 100,
  min = 0,
  rounded = false,
  size = "large",
  theme = "secondary",
  value = 0,
  variant = "indeterminate",
  ...props
}: Progress.Props) => {
  // This returns a normalized percentage between a min and max value.
  const normalizedValue = ((value - min) * 100) / (max - min);

  // This returns a negative inverse value needed for styling.
  const inverseValue = normalizedValue - 100;

  const classList = cx(
    className,
    styles.progress,
    rounded && styles.progressRounded,
    styles[`progress${upperFirst(size)}` as keyof typeof styles],
    styles[`progress${upperFirst(theme)}` as keyof typeof styles],
    styles[`progress${upperFirst(variant)}` as keyof typeof styles]
  );

  return (
    <div {...props} className={classList}>
      {variant === "determinate" ? (
        <div
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={normalizedValue}
          className={styles.progressBar}
          role="progressbar"
          style={{ transform: `translateX(${inverseValue}%)` }}
        >
          <span className={styles.progressLabel}>{normalizedValue}</span>
        </div>
      ) : (
        <div className={styles.progressBar} role="progressbar">
          <span className={styles.progressLabel}>{normalizedValue}</span>
        </div>
      )}
    </div>
  );
};

export default Progress;
